// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Outsize — the pure game state. No DOM, no clock, no network: a pick in, a new
// state out. This is what the tests, the sim and the solver all drive. Every pair
// comes from the deterministic sequence in seed.ts, so nothing crosses the wire.

import { buildSequence, type Pair } from './seed';
import type { Mode } from './modes';

export type Side = 'left' | 'right';
export type EndReason = 'wrong' | 'timeout' | 'maxed';

export interface Answer {
  /** Index into the sequence of the pair answered. */
  idx: number;
  /** Which card the player picked, or null on a timeout. */
  picked: Side | null;
  correct: boolean;
}

export interface GameState {
  modeId: string;
  seed: number;
  seq: Pair[];
  /** Position in the sequence — the current pair, and the streak of correct picks. */
  idx: number;
  streak: number;
  over: boolean;
  reason: EndReason | null;
  /** Per-pair time budget (ms); 0 means untimed (solo). */
  pairMs: number;
  pairMsLeft: number;
  answers: Answer[];
}

export function createGame(mode: Mode, seed: number, timed = false): GameState {
  const seq = buildSequence(mode, seed);
  const pairMs = timed ? mode.pairMs : 0;
  return {
    modeId: mode.id,
    seed: seed >>> 0,
    seq,
    idx: 0,
    streak: 0,
    over: false,
    reason: null,
    pairMs,
    pairMsLeft: pairMs,
    answers: [],
  };
}

/** The pair on the table now, or null once the game is over / sequence exhausted. */
export function current(state: GameState): Pair | null {
  return state.idx < state.seq.length ? state.seq[state.idx] : null;
}

/** Which side of a pair holds the greater value. The floor guarantees no tie. */
export function biggerSide(pair: Pair): Side {
  return pair.leftVal > pair.rightVal ? 'left' : 'right';
}

/** The correct side for the current pair (for the reveal / "what you missed"). */
export function correctSide(state: GameState): Side | null {
  const p = current(state);
  return p ? biggerSide(p) : null;
}

/**
 * Play a pick. Pure: returns a NEW state, or the SAME state if the game is already
 * over. A correct pick banks the streak and advances (resetting the per-pair clock);
 * surviving the whole sequence is a "maxed" win. A wrong pick ends the run.
 */
export function guess(state: GameState, side: Side): GameState {
  if (state.over) return state;
  const pair = current(state);
  if (!pair) return state;
  const correct = side === biggerSide(pair);
  const answers = [...state.answers, { idx: state.idx, picked: side, correct }];
  if (!correct) {
    return { ...state, over: true, reason: 'wrong', answers };
  }
  const idx = state.idx + 1;
  const streak = state.streak + 1;
  if (idx >= state.seq.length) {
    return { ...state, idx, streak, over: true, reason: 'maxed', answers };
  }
  return { ...state, idx, streak, answers, pairMsLeft: state.pairMs };
}

/** Advance the per-pair clock (versus only). A timeout is a wrong pick. */
export function tickTimer(state: GameState, dtMs: number): GameState {
  if (state.over || state.pairMs <= 0) return state;
  const left = state.pairMsLeft - dtMs;
  if (left <= 0) {
    const answers = [...state.answers, { idx: state.idx, picked: null, correct: false }];
    return { ...state, pairMsLeft: 0, over: true, reason: 'timeout', answers };
  }
  return { ...state, pairMsLeft: left };
}

/** The pair whose answer ended the run (for the results reveal), or null. */
export function fatalAnswer(state: GameState): Answer | null {
  const last = state.answers[state.answers.length - 1];
  return last && !last.correct ? last : null;
}
